/** ACCESS_GROUPS_SETUP control: pick + save the Access-Group template group (step 1),
 *  then materialise it into Access Groups + scoped permissions (step 2).
 *
 *  Step 1 (select_template) writes config.onboarding.ag_template_group. Step 2
 *  (materialize) runs the materialisation — an explicit ops action, not the auto-run
 *  sweep, so it works regardless of the autorun kill-switch. Reads the available
 *  template groups + current selection from item.detail.
 */
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { AgroupArgumentsForm } from '@/features/rbac/AgroupArgumentsForm';

interface TemplateOption {
  id: number;
  name: string;
}

function parseTemplates(raw: unknown): TemplateOption[] {
  if (!Array.isArray(raw)) return [];
  const out: TemplateOption[] = [];
  for (const t of raw) {
    if (
      t &&
      typeof t === 'object' &&
      typeof (t as { id?: unknown }).id === 'number' &&
      typeof (t as { name?: unknown }).name === 'string'
    ) {
      out.push({ id: (t as TemplateOption).id, name: (t as TemplateOption).name });
    }
  }
  return out;
}

export function AccessGroupsSetupControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const available = parseTemplates(item.detail.available_templates);
  const persistedId =
    typeof item.detail.selected_template_group === 'number' ? item.detail.selected_template_group : null;
  // With exactly one template group on the platform there is no choice to make,
  // so the picker arrives on it and the operator confirms rather than chooses.
  // It is only PRESELECTED — Save still writes it, so nothing high-impact (this
  // materialises Access Groups and scoped permissions) happens on a guess.
  const onlyId = available.length === 1 && available[0] ? String(available[0].id) : '';
  const [selected, setSelected] = useState<string>(
    persistedId === null ? onlyId : String(persistedId),
  );
  const dirty = selected !== (persistedId === null ? '' : String(persistedId));

  // Argument aliases the persisted group needs (from detail()); each is a 1+ access-tag multiselect.
  const requiredArgs: string[] = Array.isArray(item.detail.required_arguments)
    ? (item.detail.required_arguments as unknown[]).filter((a): a is string => typeof a === 'string')
    : [];
  const [argValues, setArgValues] = useState<Record<string, string[]>>({});
  const argsComplete = requiredArgs.every((a) => (argValues[a] ?? []).length > 0);

  const save = useMutation({
    mutationFn: () => items.selectTemplate(topId, item.id, selected === '' ? null : Number(selected)),
    onSuccess: () => {
      snackbar.show('Template group saved.', 'success');
      onChanged();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const materialize = useMutation({
    mutationFn: () => items.materialize(topId, item.id, argValues),
    onSuccess: (res) => {
      const failed = !res.run || res.run.status === 'error';
      snackbar.show(
        failed ? res.run?.error_message || 'Materialisation failed.' : 'Access groups materialised.',
        failed ? 'error' : 'success',
      );
      onChanged();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Materialisation failed.', 'error'),
  });

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        <FormControl size="small" fullWidth disabled={!canManage || save.isPending}>
          <InputLabel id={`ag-template-${item.id}`}>Access-group template</InputLabel>
          <Select
            labelId={`ag-template-${item.id}`}
            label="Access-group template"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {available.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canManage && persistedId !== null && !dirty && requiredArgs.length > 0 && (
          <AgroupArgumentsForm
            aliases={requiredArgs}
            topId={topId}
            values={argValues}
            onChange={setArgValues}
          />
        )}

        {canManage && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="outlined" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              Save selection
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={persistedId === null || dirty || materialize.isPending || !argsComplete}
              onClick={() => materialize.mutate()}
            >
              Materialise
            </Button>
          </Stack>
        )}

        {canManage && dirty && (
          <Alert severity="info" sx={{ py: 0 }}>
            Save the selection before materialising.
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
